import React, { useEffect } from 'react';

import styled from '@emotion/styled';

import ChartWrapper from '../../../components/Charts/ChartWrapper';
import DoughnutChart from '../../../components/Charts/DoughnutChart';
import PieChart from '../../../components/Charts/PieChart';
import useDriverTypeDistribution from '../../../hooks/dashboard/dashboard/useDriverTypeDistribution';
import useVehicleStatus from '../../../hooks/dashboard/dashboard/useVehicleStatus';
import { useAppSelector } from '../../../redux/store';

const DashboardChart = () => {
  const { getDriverTypeDistribution } = useDriverTypeDistribution();
  const { getVehicleStatus } = useVehicleStatus();
  const { vehicleStatus } = useAppSelector((state) => state.vehicleStatusData);
  const { driverTypeDistribution } = useAppSelector(
    (state) => state.driverTypeDistributionData,
  );

  useEffect(() => {
    getDriverTypeDistribution();
    getVehicleStatus();

    // eslint-disable-next-line
  }, []);

  const totalVehicles =
    vehicleStatus?.reduce((sum, item) => sum + item.value, 0) ?? 0;

  return (
    <Container>
      <ChartWrapper label='Vehicle status' maxWidth='100%' radius='8px'>
        <ChartRow>
          <DonutArea>
            <DoughnutChart
              data={(vehicleStatus ?? []) as any}
              showLegend={false}
              tooltipLabel='Vehicle Status'
              centerLabel='Total Vehicles'
              centerValue={totalVehicles}
              aspect={1}
              innerRadius='72%'
              outerRadius='84%'
            />
          </DonutArea>
          <LegendArea>
            {vehicleStatus?.map((item) => (
              <LegendRow key={item.name}>
                <LegendLabelBlock>
                  <LegendDot color={item.color ?? '#5B91F5'} />
                  <LegendText>{item.name}</LegendText>
                </LegendLabelBlock>
                <LegendValue>{item.value}</LegendValue>
              </LegendRow>
            ))}
          </LegendArea>
        </ChartRow>
      </ChartWrapper>

      <ChartWrapper
        label='Driver type distribution'
        maxWidth='100%'
        radius='8px'
      >
        <ChartRow>
          <PieArea>
            <PieChart
              data={(driverTypeDistribution ?? []) as any}
              aspect={1}
              outerRadius={96}
              cx='50%'
              cy='50%'
              showLabels={false}
              showLegend={false}
              tooltipValueFormatter={(value) => `${value}`}
            />
          </PieArea>
          <LegendArea>
            {driverTypeDistribution?.map((item) => (
              <LegendRow key={item.name}>
                <LegendLabelBlock>
                  <LegendDot color={item.color ?? '#5B91F5'} />
                  <LegendText>{item.name}</LegendText>
                </LegendLabelBlock>
                <LegendValue>{item.value}</LegendValue>
              </LegendRow>
            ))}
          </LegendArea>
        </ChartRow>
      </ChartWrapper>
    </Container>
  );
};

export default DashboardChart;

const Container = styled.div`
  height: fit-content;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const ChartRow = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: minmax(190px, 260px) 1fr;
  align-items: center;
  gap: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 14px;
  }
`;

const DonutArea = styled.div`
  width: 100%;
  max-width: 240px;
  min-width: 220px;
  margin: 0 auto;
`;

const PieArea = styled.div`
  width: 100%;
  max-width: 240px;
  min-width: 220px;
  margin: 0 auto;
`;

const LegendArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const LegendRow = styled.div`
  display: grid;
  grid-template-columns: 132px auto;
  gap: 16px;
`;

const LegendLabelBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const LegendText = styled.span`
  font-size: 14px;
  line-height: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.neutralGray};
`;

const LegendValue = styled.span`
  font-size: 14px;
  line-height: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.logirateBlack};
`;
